export const DEFAULT_FAQS = [
  { question: 'Do I need an appointment, or do you accept walk-ins?', answer: '' },
  { question: 'Do you offer virtual/tele-consultations?', answer: '' },
  { question: 'Do you accept insurance?', answer: '' },
  { question: 'What insurance plans do you accept?', answer: '' },
  { question: 'What should I bring to my appointment?', answer: '' },
  { question: 'How often should I schedule a routine checkup?', answer: '' },
  { question: 'Do you handle medical emergencies?', answer: '' },
  { question: 'What are your business hours?', answer: '' },
  { question: 'How do I cancel or reschedule my appointment?', answer: '' },
  { question: 'What is your cancellation policy?', answer: '' },
  { question: 'How early should I arrive for my appointment?', answer: '' },
  { question: 'Do you offer payment plans or financing options?', answer: '' },
  { question: 'What forms of payment do you accept?', answer: '' },
  { question: 'Is parking available at your location?', answer: '' },
  { question: 'Do you have wheelchair accessibility?', answer: '' },
  { question: 'Can I request a specific provider/doctor?', answer: '' },
  { question: 'Do you offer services for new patients?', answer: '' },
  { question: 'What is the typical wait time for an appointment?', answer: '' },
  { question: 'Do you provide prescription refills?', answer: '' },
  { question: 'How can I access my medical records?', answer: '' }
];

export const OnboardingFormDefaultValues = {
  faqs: DEFAULT_FAQS,
  integrations: [],
  telephony_provider_ids: [],
  sampleCallRecordings: [],
  office_operations_files_attributes: [],
  capabilities: {
    appointmentBooking: {
      enabled: false,
      providers: []
    },
    serviceBooking: { enabled: false },
    orderBooking: { enabled: false, orderTypes: [] },
    transferCall: {
      enabled: false,
      transferType: 'warm'
    },
    duringCallSms: {
      enabled: false,
      template: '',
      when: ''
    },
    postCallSms: {
      enabled: false,
      message: ''
    },
    postCallEmail: {
      enabled: false,
      subject: '',
      body: ''
    },
    takeNote: {
      enabled: false
    },
    spamTermination: {
      enabled: false,
      message: '',
      blockUnknown: false
    },
    emergencyHandling: {
      enabled: false,
      mode: 'message',
      message: '',
      number: ''
    }
  },
  address: {
    time_zone: ''
  },
  business_hours: {},
  location_details: {
    count: 1,
    physical_location_exists: true
  },
  transfer_call_details: [
    {
      transfer_type: 'operator',
      transfer_mode: 'warm',
      trigger: '',
      sources: [
        {
          target_id: '',
          target_name: '',
          target_type: '',
          target_value: ''
        }
      ]
    }
  ],
  custom_keywords: []
};

export const STEP_FIELDS: Record<number, string[]> = {
  0: [
    'business_legal_name',
    'website_url',
    'primary_contact_name',
    'primary_contact_email',
    'primary_contact_phone',
    'address.street_address',
    'address.city',
    'address.postal_zip_code',
    'address.state_province_region',
    'address.country'
  ],
  1: ['office_operations_files_attributes'],
  2: [
    'location_details.count',
    'location_details.physical_location_exists',
    'location_details.location_name',

    'address.time_zone',

    // Physical only fields (handled dynamically in UI)
    'location_details.street',
    'location_details.address2',
    'location_details.city',
    'location_details.zip',
    'location_details.country',
    'location_details.state',

    'business_hours'
  ],
  3: [
    'operator_details.name',
    'operator_details.email',
    'operator_details.role',
    'operator_details.phone',
    'operator_details.sms_consent',
    'operator_details.working_hours',
    'operator_details.main_line_number',
    'operator_details.extension',
    'operator_details.sip_uri'
  ],
  4: [
    // Toggles
    'appointment_booking_enable',
    'appointment_scheduling_enable',
    'order_booking_enable',
    'transfer_call_enable',
    'send_sms_to_caller_enable',
    'post_notify_via_sms_enable',
    'post_notify_via_email_enable',
    'note_taking_enable',
    'spam_call_termination_enable',
    'emergency_call_handling_enable'
  ],
  // Integration Details
  5: ['available_integration_ids'],
  // Knowledge Base
  6: ['faq', 'knowledgebase_files_attributes'],
  // Customer Sentiments
  7: ['custom_keywords'],
  // Telephony Details
  8: [
    'current_business_solution.business_phone_number',
    'current_business_solution.telephony_voip_provider',
    'current_business_solution.configuration.sip_credentials'
  ],
  // Additional Info
  9: ['additional_note', 'additional_documents_attributes', 'confirmation'],
  // Internal Process
  10: [
    'business_plan_id',
    'voice_engine_id',
    'ticket_reference',
    'coupon_code',
    'stripe_customer_id',
    'telephony_provider_ids',
    'interoffice_notes'
  ]
};

export const STEP_REQUIRED_FIELDS: Record<number, string[]> = {
  0: [
    'business_legal_name',
    'website_url',
    'primary_contact_name',
    'primary_contact_email',
    'primary_contact_phone',
    'address.street_address',
    'address.city',
    'address.postal_zip_code',
    'address.state_province_region',
    'address.country'
  ],

  2: ['location_details.count', 'location_details.location_name', 'location_details.country', 'location_details.state'],

  3: ['operator_details'],

  5: ['available_integration_ids'],

  10: ['business_plan_id', 'voice_engine_id']
};

const EMERGENCY_DEFAULT_MESSAGE_OTHERS =
  "If you're experiencing an emergency requiring immediate assistance, please hang up and call 911. We're unable to assist with emergency situations. Goodbye.";

export const EMERGENCY_DEFAULT_MESSAGES_BY_INDUSTRY: Record<string, string> = {
  healthcare:
    "If you're experiencing a medical emergency, please hang up and call 911 or visit your nearest emergency department immediately. We're unable to assist with emergency situations. Goodbye.",
  field_service:
    "If this is an emergency involving a gas leak, electrical hazard, fire, flooding, or another immediate safety risk, please hang up and call 911. We're unable to assist with emergency situations. Goodbye.",
  beauty_and_wellness:
    "If you're experiencing a severe allergic reaction, excessive bleeding, or another medical emergency, please hang up and call 911 immediately. We're unable to assist with emergency situations. Goodbye.",
  restaurant: EMERGENCY_DEFAULT_MESSAGE_OTHERS,
  others: EMERGENCY_DEFAULT_MESSAGE_OTHERS
};

export function getEmergencyDefaultMessage(industry?: string): string {
  if (!industry) return EMERGENCY_DEFAULT_MESSAGE_OTHERS;
  return EMERGENCY_DEFAULT_MESSAGES_BY_INDUSTRY[industry] ?? EMERGENCY_DEFAULT_MESSAGE_OTHERS;
}

const EARLY_ONBOARDING_STATUSES = new Set(['draft', 'collecting_agent_info']);

export function shouldApplyDefaultAgentCapabilities(form?: {
  status?: string;
  note_taking_enable?: boolean;
  spam_call_termination_enable?: boolean;
  emergency_call_handling_enable?: boolean;
}): boolean {
  if (!form || !EARLY_ONBOARDING_STATUSES.has(form.status || '')) return false;
  return !form.note_taking_enable && !form.spam_call_termination_enable && !form.emergency_call_handling_enable;
}
